import React, { useState, useEffect } from 'react';
import { Translation } from 'react-i18next';
import { Form, Input, Space, Radio, Row, Col, InputNumber } from 'antd';
import ModuleCategoryInputRow from '../containers/ModuleCategoryInputRowContainer';
import CoverImageUpload from './CoverImageUpload';
import MediaUpload from './MediaUpload';
import QuillEditor from './QuillEditor';

const ModuleInputRow = ({
  index,
  disabled,
  module,
  form,
  setIsChangeValue,
  moduleValue,
  getSignedUrl,
  setDeleteImageIds,
  deleteImageIds,
  trackerActions,
}) => {
  const [uploadType, setUploadType] = useState(1);
  const [isContentChange, setContentChange] = useState(false);

  useEffect(() => {
    if (moduleValue?.media) {
      setUploadType(1);
      form.setFields([
        {
          name: ['modules', index, 'upload_content'],
          value: 1,
        },
      ]);
    } else if (moduleValue?.video_youtube_url) {
      form.setFields([
        {
          name: ['modules', index, 'upload_content'],
          value: 2,
        },
      ]);
      setUploadType(2);
    } else if (moduleValue?.video_vimeo_url) {
      form.setFields([
        {
          name: ['modules', index, 'upload_content'],
          value: 3,
        },
      ]);
      setUploadType(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    moduleValue?.media,
    moduleValue?.video_youtube_url,
    moduleValue?.video_vimeo_url,
  ]);

  return (
    <Translation>
      {(t) => (
        <div style={{ padding: '0 30px' }}>
          <div className='form-group'>
            <div
              className='ant-col ant-col-24 ant-form-item-label'
              style={{ paddingBottom: '0' }}
            >
              <label className='ant-form-item-required'>
                {t('learning_admin_form_input_title')}
              </label>
            </div>
            <Form.Item
              name={[module.name, 'title']}
              fieldKey={[module.fieldKey, 'title']}
              rules={[
                {
                  required: true,
                  message: t('feedback_validation_required'),
                },
                {
                  type: 'string',
                  min: 1,
                  max: 60,
                  message: t('feedback_validation_char_range', {
                    min: 1,
                    max: 60,
                  }),
                },
              ]}
            >
              <Input disabled={disabled} />
            </Form.Item>
          </div>

          <div className='form-group'>
            <div
              className='ant-col ant-col-24 ant-form-item-label'
              style={{ paddingBottom: '0' }}
            >
              <label className='ant-form-item-required'>
                {t('learning_admin_form_input_description')}
              </label>
            </div>
            <Form.Item
              name={[module.name, 'description']}
              fieldKey={[module.fieldKey, 'description']}
              rules={[
                {
                  required: true,
                  message: t('feedback_validation_required'),
                },
                {
                  type: 'string',
                  min: 1,
                  max: 100,
                  message: t('feedback_validation_char_range', {
                    min: 1,
                    max: 100,
                  }),
                },
              ]}
            >
              <Input.TextArea disabled={disabled} />
            </Form.Item>
          </div>

          <CoverImageUpload
            form={form}
            field={module}
            index={index}
            cover_image={moduleValue?.cover_image}
            cover_image_id={moduleValue?.cover_image_id}
            getSignedUrl={getSignedUrl}
            setDeleteImageIds={setDeleteImageIds}
            deleteImageIds={deleteImageIds}
          />

          <div className='form-group'>
            <div
              className='ant-col ant-col-24 ant-form-item-label'
              style={{ paddingBottom: '0' }}
            >
              <label className='ant-form-item-required'>
                {t('learning_admin_form_input_upload_type')}
              </label>
            </div>
            <Form.Item
              name={[module.name, 'upload_content']}
              fieldKey={[module.fieldKey, 'upload_content']}
              rules={[
                {
                  required: true,
                  message: t('feedback_validation_required'),
                },
              ]}
            >
              <Radio.Group
                size='large'
                onChange={(e) => setUploadType(e.target.value)}
                defaultValue={1}
              >
                <Space direction='horizontal'>
                  <Radio value={1}>
                    {t('learning_admin_form_input_upload_type_1')}
                  </Radio>
                  <Radio value={2}>
                    {t('learning_admin_form_input_upload_type_2')}
                  </Radio>
                  <Radio value={3}>
                    {t('learning_admin_form_input_upload_type_3')}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </div>

          {uploadType === 1 && (
            <MediaUpload
              form={form}
              field={module}
              index={index}
              media={moduleValue?.media}
              media_id={moduleValue?.media_id}
              getSignedUrl={getSignedUrl}
              setDeleteImageIds={setDeleteImageIds}
              deleteImageIds={deleteImageIds}
            />
          )}

          {uploadType === 2 && (
            <div className='form-group'>
              <div
                className='ant-col ant-col-24 ant-form-item-label'
                style={{ paddingBottom: '0' }}
              >
                <label className='ant-form-item'>
                  {t('learning_admin_form_input_video_youtube_url')}
                </label>
              </div>
              <Form.Item
                name={[module.name, 'video_youtube_url']}
                fieldKey={[module.fieldKey, 'video_youtube_url']}
                rules={[
                  {
                    required: true,
                    message: t('feedback_validation_required'),
                  },
                  {
                    pattern:
                      /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(?:-nocookie)?\.com|youtu\.be))(\/(?:[\w-]+\?v=|embed\/|live\/|v\/)?)([\w-]+)(\S+)?$/,
                    message: t('feedback_validation_url'),
                  },
                ]}
              >
                <Input disabled={disabled} />
              </Form.Item>
            </div>
          )}

          {uploadType === 3 && (
            <div className='form-group'>
              <div
                className='ant-col ant-col-24 ant-form-item-label'
                style={{ paddingBottom: '0' }}
              >
                <label className='ant-form-item'>
                  {t('learning_admin_form_input_video_vimeo_url')}
                </label>
              </div>
              <Form.Item
                name={[module.name, 'video_vimeo_url']}
                fieldKey={[module.fieldKey, 'video_vimeo_url']}
                rules={[
                  {
                    required: true,
                    message: t('feedback_validation_required'),
                  },
                  {
                    pattern: new RegExp(
                      '^(?:https?:\\/\\/)?(?:www\\.)?(?:player\\.)?vimeo\\.com\\/(?:channels\\/(?:\\w+\\/)?|groups\\/[^/]*\\/videos\\/|video\\/)?(\\d+)(?:|/?)?$'
                    ),
                    message: t('feedback_validation_url'),
                  },
                ]}
              >
                <Input disabled={disabled} />
              </Form.Item>
            </div>
          )}

          <Row gutter={48}>
            <Col xs={{ span: 24, order: 1 }} sm={{ span: 12, order: 1 }}>
              <div className='form-group'>
                <div
                  className='ant-col ant-col-24 ant-form-item-label'
                  style={{ paddingBottom: '0' }}
                >
                  <label className='ant-form-item-required'>
                    {t('learning_admin_form_input_order')}
                  </label>
                </div>
                <Form.Item
                  name={[module.name, 'order']}
                  rules={[
                    {
                      required: true,
                      message: t('feedback_validation_required'),
                    },
                    {
                      type: 'number',
                      message: t('feedback_validation_number'),
                    },
                    {
                      type: 'number',
                      min: 0,
                      message: t('feedback_validation_range', { min: 0 }),
                    },
                    {
                      validator: async (_, value) => {
                        const modules = form.getFieldValue('modules') || [];
                        const total = modules.length;

                        if (value < 1 || value > total) {
                          return Promise.reject(
                            new Error(
                              `The order must be between 1 and ${total}`
                            )
                          );
                        }

                        const currentOrders = modules.map((m, idx) =>
                          idx === module.name ? value : m?.order
                        );
                        const hasDuplicates =
                          currentOrders.filter((v) => v !== undefined)
                            .length !== new Set(currentOrders).size;

                        if (hasDuplicates) {
                          return Promise.reject(
                            new Error('The order already exists')
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    disabled={disabled}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={{ span: 24, order: 2 }} sm={{ span: 12, order: 2 }}>
              <div className='form-group'>
                <div
                  className='ant-col ant-col-24 ant-form-item-label'
                  style={{ paddingBottom: '0' }}
                >
                  <label className='ant-form-item'>
                    {t('learning_admin_form_input_assign_category')}
                  </label>
                </div>
                <ModuleCategoryInputRow
                  index={index}
                  name={[module.name, 'categories_ids']}
                  fieldKey={[module.fieldKey, 'categories_ids']}
                  disabled={disabled}
                  form={form}
                  trackerActions={trackerActions}
                />
              </div>
            </Col>
          </Row>

          <div className='form-group'>
            <div
              className='ant-col ant-col-24 ant-form-item-label'
              style={{ paddingBottom: '0' }}
            >
              <label className='ant-form-item-required'>
                {t('learning_admin_form_input_content')}
              </label>
            </div>
            <Form.Item
              name={[module.name, 'content']}
              rules={[
                {
                  required: true,
                  message: t('feedback_validation_required'),
                },
              ]}
            >
              <QuillEditor
                disabled={disabled}
                index={index}
                isContentChange={isContentChange}
                setIsChangeValue={setIsChangeValue}
                setContentChange={setContentChange}
              />
            </Form.Item>
          </div>
        </div>
      )}
    </Translation>
  );
};

export default ModuleInputRow;
